import React, { useState, useRef, useEffect } from 'react';
import { adminService } from '../../../../services';
import type { User } from '../../../../types/auth';

interface Props {
  selected: User[];
  onChange: (users: User[]) => void;
}

const initials = (u: User) =>
  `${u.firstName[0] ?? ''}${u.lastName[0] ?? ''}`.toUpperCase();

const UserSearchSelect: React.FC<Props> = ({ selected, onChange }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = (q: string) => {
    setQuery(q);
    if (q.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const all = await adminService.getUsers();
        const ql = q.toLowerCase();
        setResults(
          all.filter(
            (u) =>
              !selected.find((s) => s.id === u.id) &&
              (u.firstName.toLowerCase().includes(ql) ||
                u.lastName.toLowerCase().includes(ql) ||
                u.email.toLowerCase().includes(ql) ||
                u.id.toLowerCase().includes(ql))
          )
        );
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const addUser = (u: User) => {
    onChange([...selected, u]);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const removeUser = (id: string) =>
    onChange(selected.filter((u) => u.id !== id));

  return (
    <div className="ac-user-search" ref={wrapRef}>
      {selected.length > 0 && (
        <div className="ac-user-chips">
          {selected.map((u) => (
            <div key={u.id} className="ac-user-chip">
              <div className="ac-user-chip__avatar">{initials(u)}</div>
              <span className="ac-user-chip__name">
                {u.firstName} {u.lastName}
              </span>
              <button
                type="button"
                className="ac-user-chip__remove"
                onClick={() => removeUser(u.id)}
                aria-label={`Rimuovi ${u.firstName}`}
              >
                <i className="bi bi-x" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="ac-user-search__input-wrap">
        <i className="bi bi-search ac-user-search__icon" aria-hidden="true" />
        <input
          type="text"
          className="ac-input ac-user-search__input"
          placeholder="Cerca per nome, cognome o email…"
          value={query}
          onChange={(e) => search(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          autoComplete="off"
        />
        {loading && <span className="ac-user-search__spinner" />}
      </div>

      {open && (
        <div className="ac-user-search__dropdown" role="listbox">
          {results.length === 0 ? (
            <p className="ac-user-search__empty">Nessun utente trovato.</p>
          ) : (
            results.map((u) => (
              <button
                key={u.id}
                type="button"
                className="ac-user-search__option"
                onClick={() => addUser(u)}
                role="option"
                aria-selected="false"
              >
                <div className="ac-user-search__avatar">{initials(u)}</div>
                <div className="ac-user-search__info">
                  <span className="ac-user-search__fullname">
                    {u.firstName} {u.lastName}
                  </span>
                  <span className="ac-user-search__email">{u.email}</span>
                </div>
                <i className="bi bi-plus-circle ac-user-search__add" />
              </button>
            ))
          )}
        </div>
      )}

      {selected.length === 0 && (
        <p className="ac-field-hint">
          Digita almeno 2 caratteri per cercare. Puoi selezionare più utenti.
        </p>
      )}
    </div>
  );
};

export default UserSearchSelect;
